import { useMemo } from 'react';
import { findById, type EditorDoc } from '../lib/model';
import { getAttr, rootElement, walkElements } from '../lib/xml';
import { useDispatch, useFeatures, useStore } from '../state/store';

/** Plain-English help for the attributes DayZ uses on zones and positions. */
const ATTR_HELP: Record<string, string> = {
  name: 'Zone name - must be unique inside the file.',
  x: 'East / west position in metres.',
  y: 'Height. DayZ normally ignores this and snaps to the terrain.',
  z: 'North / south position in metres.',
  r: 'Radius of the territory in metres.',
  a: 'Yaw (facing) in degrees.',
  smin: 'Static minimum: animals spawned when the server starts.',
  smax: 'Static maximum: animals spawned when the server starts.',
  dmin: 'Dynamic minimum: animals spawned around players.',
  dmax: 'Dynamic maximum: animals spawned around players.',
};

const NUMERIC = new Set(['x', 'y', 'z', 'r', 'a', 'smin', 'smax', 'dmin', 'dmax']);

export function Inspector() {
  const state = useStore();
  const dispatch = useDispatch();
  const { byId } = useFeatures();

  const selected = state.selection.map((id) => byId.get(id)).filter(Boolean);
  const first = selected[0];
  const doc = first ? state.docs.find((d) => d.id === first.docId) : undefined;

  const attrs = useMemo(() => {
    if (!doc || !first) return [];
    const el = findById(doc.doc, first.id);
    return el ? el.attrs.map((a) => ({ ...a })) : [];
  }, [doc, first]);

  return (
    <div className="panel">
      <h2>Inspector</h2>

      {!first && <p className="empty">Select a position on the map to edit its values.</p>}

      {first && doc && (
        <>
          <div className="inspector-head">
            <b>{selected.length === 1 ? `<${first.tag}>` : `${selected.length} selected`}</b>
            <span className="muted">{doc.fileName}</span>
          </div>

          <div className="fields">
            {attrs.map((attr) => (
              <label key={attr.name} title={ATTR_HELP[attr.name] ?? ''}>
                <span>{attr.name}</span>
                <input
                  type={NUMERIC.has(attr.name) ? 'number' : 'text'}
                  step={attr.name === 'r' ? 10 : 1}
                  value={attr.value}
                  onChange={(e) =>
                    dispatch({
                      type: 'setFeatureAttrs',
                      ids: selected.length > 1 && attr.name !== 'name' ? state.selection : [first.id],
                      attrs: { [attr.name]: e.target.value },
                    })
                  }
                />
              </label>
            ))}
          </div>

          {selected.length > 1 && (
            <p className="hint">
              Editing a field applies it to all {selected.length} selected positions ( <code>name</code> stays
              per-position).
            </p>
          )}

          {attrs.some((a) => a.name === 'r') && (
            <div className="quick-row">
              {[100, 250, 500, 1000].map((radius) => (
                <button
                  key={radius}
                  type="button"
                  onClick={() =>
                    dispatch({ type: 'setFeatureAttrs', ids: state.selection, attrs: { r: radius.toFixed(1) } })
                  }
                >
                  r={radius}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <Checks />
    </div>
  );
}

type Problem = { level: 'error' | 'warn'; text: string };

function Checks() {
  const state = useStore();
  const { features } = useFeatures();

  const problems = useMemo<Problem[]>(() => {
    const list: Problem[] = [];
    const size = state.mapSize;

    const outside = features.filter((f) => f.x < 0 || f.z < 0 || f.x > size || f.z > size);
    if (outside.length > 0) {
      list.push({
        level: 'error',
        text: `${outside.length} position(s) are outside the ${size} x ${size} m map and will not spawn.`,
      });
    }

    // Event names in cfgeventspawns.xml must exist in events.xml.
    const spawnDocs = state.docs.filter((d) => d.kind === 'eventspawns');
    const eventDocs = state.docs.filter((d) => d.kind === 'events');
    if (spawnDocs.length > 0 && eventDocs.length > 0) {
      const defined = new Set<string>();
      for (const doc of eventDocs) {
        for (const { el } of walkElements(doc.doc)) {
          if (el.tag === 'event') {
            const name = getAttr(el, 'name');
            if (name) defined.add(name);
          }
        }
      }
      const missing = new Set<string>();
      for (const doc of spawnDocs) {
        const root = rootElement(doc.doc);
        for (const child of root?.children ?? []) {
          if (child.kind !== 'element' || child.tag !== 'event') continue;
          const name = getAttr(child, 'name');
          if (name && !defined.has(name)) missing.add(name);
        }
      }
      if (missing.size > 0) {
        list.push({
          level: 'warn',
          text: `Spawn positions reference event(s) not defined in events.xml: ${[...missing].join(', ')}.`,
        });
      }
    }

    for (const doc of state.docs.filter((d) => d.kind === 'territories')) {
      list.push(...checkTerritoryCounts(doc));
    }

    return list;
  }, [features, state.docs, state.mapSize]);

  return (
    <div className="checks">
      <h2>Checks</h2>
      {problems.length === 0 ? (
        <p className="ok">No problems found.</p>
      ) : (
        <ul>
          {problems.map((problem, index) => (
            <li key={index} className={problem.level}>
              {problem.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function checkTerritoryCounts(doc: EditorDoc): Problem[] {
  const problems: Problem[] = [];
  for (const { el } of walkElements(doc.doc)) {
    if (el.tag !== 'zone') continue;
    const smin = Number.parseFloat(getAttr(el, 'smin') ?? '0');
    const smax = Number.parseFloat(getAttr(el, 'smax') ?? '0');
    const dmin = Number.parseFloat(getAttr(el, 'dmin') ?? '0');
    const dmax = Number.parseFloat(getAttr(el, 'dmax') ?? '0');
    const name = getAttr(el, 'name') ?? '(unnamed)';
    if (smax < smin || dmax < dmin) {
      problems.push({ level: 'error', text: `${doc.fileName}: zone "${name}" has a max lower than its min.` });
    }
  }
  return problems.slice(0, 5);
}
